// i18n for the ptc-supervision STAFF UI (fleet i18n standard; statetax is the reference).
//
// Scope fence on what gets translated:
// - YES: UI chrome — nav, page titles, buttons, labels, empty/error messages, confirmations.
// - NO:  data — client names, staff names, Dataverse/CRM values, anything a person typed.
// - NO:  client-facing text (review emails, SMS, the upload link). Those follow the CLIENT's
//        recorded language on their own path and are never routed through this instance.
// - NO:  dates and numbers as text — date-fns / Intl format them.
//
// Keys are hierarchical and grouped by surface: `shell.*`, `nav.*`, `header.*`, `reviewDetail.*`.
// Every PR lands both languages: add the key to locales/en.json AND locales/es.json (CI fails on a
// key in one and not the other — `npm run i18n:check`), and list the Spanish string in
// review/es-status.json as "machine" until a native reviewer signs it off (`npm run i18n:review`).
//
// Import `useTranslation` from HERE, not from react-i18next: importing this module is what
// initialises the instance, so a component (or a test that renders one) can never meet an
// uninitialised i18next and render raw keys.

import i18n from 'i18next';
import { initReactI18next, Trans, useTranslation } from 'react-i18next';
import en from './locales/en.json';
import es from './locales/es.json';
import { LAST_USED_KEY, readStored, resolveLanguage, writeStored, type UiLanguage } from './language';

const initial = resolveLanguage({
  choice: readStored(LAST_USED_KEY),
  browser: typeof navigator !== 'undefined' ? navigator.language : null,
});

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, es: { translation: es } },
  lng: initial,
  fallbackLng: 'en',
  supportedLngs: ['en', 'es'],
  interpolation: { escapeValue: false }, // React escapes already.
  returnEmptyString: false, // an empty string in a locale falls back to English, never renders blank
  initAsync: false, // resources are bundled: initialise synchronously, so the first render is translated
});

function stampDocument(lang: string) {
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
}
stampDocument(initial);
i18n.on('languageChanged', stampDocument);

/** Switches the UI language now, and remembers it as this device's last-used language. */
export function applyUiLanguage(lang: UiLanguage): void {
  writeStored(LAST_USED_KEY, lang);
  if (i18n.language !== lang) void i18n.changeLanguage(lang);
}

export { useTranslation, Trans };
export default i18n;
